import { EpicPolicy } from '@/types/tower/Domain';
import { PolicySource } from '@/types/PolicySource';

export interface PoliciesResponse {
  policies: PolicyWithChildren[];
  stats: PolicyStats;
}

export interface PolicyStats {
  maxRetention?: number;
  maxAttachmentPoint?: number;
  totalCount?: number;
  totalPremium?: number;
}

export interface PolicyWithChildren extends EpicPolicy {
  children?: EpicPolicy[] | 'loading'
}

/**
 * Builds policies as a hierarchy. Used when results are already sorted by an API.
 */
export function GetPoliciesAsHierarchy(policies: EpicPolicy[]): PolicyWithChildren[] {
  const parents = policies.filter(x => !x.isChildPolicy);
  return parents.map(parent => {
    return { ...parent, children: policies.filter(x => x.uniqPolicy === parent.uniqPolicy && x.isChildPolicy && x !== parent) };
  });
}

/**
 * Builds policies as a hierarchy. Used when results are not sorted by an API.
 * This method is faster than GetPoliciesAsHierarchy, but the return policies will
 * need to be resorted when returned.
 */
export function PoliciesAsHierarchy(policies: EpicPolicy[]): PolicyWithChildren[] {
  const sorted = [...policies].sort((a, b) => a.policyID.localeCompare(b.policyID));
  const result: PolicyWithChildren[] = [];
  let currentPolicy: PolicyWithChildren | undefined = undefined;
  for (const policy of sorted) {
    if (policy.isChildPolicy) {
      if (currentPolicy && Array.isArray(currentPolicy.children)) {
        currentPolicy.children.push(policy);
      }
    } else {
      if (currentPolicy) {
        result.push(currentPolicy);
      }
      currentPolicy = { ...policy, children: [] } as PolicyWithChildren;
    }
  }
  if (currentPolicy) {
    result.push(currentPolicy);
  }
  return result;
}

export function FlattenPolicies(policies: PolicyWithChildren[]): EpicPolicy[] {
  return policies.reduce((acc, p) => {
    const { children, ...policy } = p;
    acc.push(policy);
    if (Array.isArray(children)) {
      children.forEach(c => acc.push(c));
    }
    return acc;
  }, [] as EpicPolicy[]);
}

type AgencyFields = Pick<EpicPolicy, 'agencyName' | 'policySourceID'>;

export function externalPolicySourceIDs(policySources: PolicySource[]): number[] {
  return policySources.filter((ps) => ps.IsExternal).map((ps) => ps.ID);
}

function isExternalPolicySource(policy: AgencyFields, externalSourceIDs: number[]): boolean {
  return policy.policySourceID !== undefined && externalSourceIDs.includes(policy.policySourceID);
}

// An external upload's agency is free text naming the outside broker, not a CAC entity.
export function externalAgencyName(policy: AgencyFields, externalSourceIDs: number[]): string | undefined {
  return isExternalPolicySource(policy, externalSourceIDs) ? policy.agencyName : undefined;
}

export function cacEntityName(policy: AgencyFields, externalSourceIDs: number[]): string | undefined {
  return isExternalPolicySource(policy, externalSourceIDs) ? undefined : policy.agencyName;
}
